import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const Logo = () => (
  /* Using a local version to avoid dependency loops */
  <div className="flex items-center gap-3">
    <div className="w-16 h-16 flex-shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <rect width="100" height="100" rx="22" fill="var(--primary-color)" />
        <text
          x="50"
          y="55"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize="48"
          fontWeight="bold"
        >
          R
        </text>
        <circle cx="80" cy="20" r="10" fill="var(--accent-color)" />
      </svg>
    </div>
    <div className="flex flex-col leading-tight">
      <span className="font-bold text-xl text-gray-800">REACT</span>
      <span className="text-sm text-gray-500">Email Configurator</span>
    </div>
  </div>
);

export const LoginScreen = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    try {
      const stored = localStorage.getItem('rememberedCredentials');
      if (stored) {
        const payload = JSON.parse(stored);
        if (payload?.username) setUsername(payload.username);
        setRememberMe(true);
      }
    } catch (err) {
      console.warn('Failed to load remembered credentials', err);
      localStorage.removeItem('rememberedCredentials');
    }
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(username, password, rememberMe);
    if (!result.success) {
      setError(
        result.message || 'Login failed. Please check your credentials.'
      );
      setLoading(false);
      return;
    }
    try {
      if (rememberMe) {
        localStorage.setItem(
          'rememberedCredentials',
          JSON.stringify({ username })
        );
      } else {
        localStorage.removeItem('rememberedCredentials');
      }
    } catch (err) {
      console.warn('Failed to persist credentials', err);
    }
    setLoading(false);
    // On success, the App component will handle the redirect.
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#050b1a] via-[#081a36] to-[#030712]">
      <div className="absolute inset-0">
        <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-gradient-to-br from-blue-500/30 via-indigo-400/15 to-transparent blur-3xl" />
        <div className="absolute bottom-[-200px] left-[-120px] h-[420px] w-[420px] rounded-full bg-gradient-to-tr from-blue-900/25 via-indigo-700/10 to-transparent blur-3xl" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
        <div className="flex w-full max-w-xl flex-col gap-8">

          <div className="mx-auto w-full rounded-3xl border border-white/10 bg-white/80 p-10 shadow-2xl backdrop-blur">
            <div className="mx-auto mb-8 flex justify-center">
              <Logo />
            </div>
            <div className="space-y-2 text-center">
              <h2 className="text-2xl font-semibold text-slate-900">Sign in to continue</h2>
            </div>
            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div className="space-y-1.5">
                <label htmlFor="login-username" className="block text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Username
                </label>
                <input
                  id="login-username"
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="e.g. jdoe"
                  autoComplete="username"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="login-password" className="block text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Your secure password"
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <label className="inline-flex items-center gap-2 font-medium text-slate-600">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                  />
                  Remember me on this device
                </label>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/20 transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
              {error && <p className="text-center text-sm font-semibold text-rose-500">{error}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
